import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { AuthProvider, RequireAuth } from "@/lib/auth";

// Eager: the sign-in page must never depend on a chunk fetch, and the audience
// join page is opened by a stranger on venue wifi with fifteen seconds of patience.
import Login from "@/platform/Login";
import AudienceScreen from "@/audience/AudienceScreen";

const DeckList = lazy(() => import("@/platform/DeckList"));
const DeckDetail = lazy(() => import("@/platform/DeckDetail"));
const DeckEditor = lazy(() => import("@/platform/DeckEditor"));
const PresentScreen = lazy(() => import("@/present/PresentScreen"));
const RemoteScreen = lazy(() => import("@/presenter/RemoteScreen"));
const TvScreen = lazy(() => import("@/present/TvScreen"));
const PromptPage = lazy(() => import("@/public/PromptPage"));

// Dev-only surfaces, behind ONE conditional import.
//
// `import.meta.env.DEV` is statically replaced, so in a production build this
// whole expression folds to `null` and Rollup drops the chunk. Guarding only the
// <Route> would leave the dynamic import in the graph and still ship it: that is
// how a 173KB chunk containing the entire course ended up in a public bundle.
const DevRoutes = import.meta.env.DEV ? lazy(() => import("@/dev/DevRoutes")) : null;

function Fallback() {
  return <div className="grid h-screen place-items-center bg-bg text-text-faint">…</div>;
}

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public: opened by scanning a code in a room. */}
          <Route path="/a/:code" element={<AudienceScreen />} />
          {/* Public: a prompt's "scan to copy" QR. One block, no sign-in. */}
          <Route path="/p/:id" element={<PromptPage />} />
          {/* A venue TV: authenticates with a key in the URL fragment, not a login. */}
          <Route path="/s/:code/display" element={<TvScreen />} />

          {DevRoutes && <Route path="/dev/*" element={<DevRoutes />} />}

          <Route element={<RequireAuth />}>
            <Route path="/" element={<DeckList />} />
            <Route path="/d/:slug" element={<DeckDetail />} />
            <Route path="/d/:slug/edit" element={<DeckEditor />} />
            {/* Full-screen, outside any shell: no sidebar, no header, no chrome
                that could steal a keystroke from the deck. */}
            <Route path="/d/:slug/present" element={<PresentScreen />} />
            <Route path="/d/:slug/remote" element={<RemoteScreen />} />
          </Route>

          <Route path="*" element={<Fallback />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
